<ng-template #bulkSimActionsModalForm let-modal>

  <app-modal-comp-header
    [title]="'Bulk Action — ' + actionLabel"
    (cancelButtonOnClick)="cancelForm()"
  ></app-modal-comp-header>

  <div class="modal-body">

    <!--#region Configure-->
    @if (step === 'configure') {
      <div class="row">
        <div class="col-lg-8 offset-lg-2">

          @if (action === BulkSimAction.ChangeProduct) {
            <div class="mb-3">
              <label class="form-label" for="bulkProduct">Product</label>
              <select id="bulkProduct"
                      class="form-select"
                      [class.is-invalid]="formSubmitted && !canProceedConfigure()"
                      [(ngModel)]="selectedProductId">
                <option [ngValue]="null" disabled>Select a product&hellip;</option>
                @for (product of products; track product.id) {
                  <option [ngValue]="product.id">{{ product.description }}</option>
                }
              </select>
              <div class="invalid-feedback">Please select a product.</div>
            </div>
          }

          @if (action === BulkSimAction.Throttle) {
            <div class="mb-3">
              <label class="form-label" for="bulkThrottle">Throttle speed</label>
              <select id="bulkThrottle" class="form-select" [(ngModel)]="selectedThrottleId">
                @for (throttle of throttleTypes; track throttle.id) {
                  <option [ngValue]="throttle.id">{{ throttle.description }}</option>
                }
              </select>
            </div>
          }

          <!-- Override Limit: full Manage-Limit-style type picker + configuration (no current-month) -->
          @if (isOverrideLimit) {

            <p class="fw-semibold mb-2">Limit Type</p>
            <div class="row g-3 mb-3 justify-content-center">
              <div class="col-4">
                <div class="limit-type-card" [class.selected]="selectedLimitType === LimitType.Off" (click)="selectLimitType(LimitType.Off)">
                  <span class="limit-card-icon"><i class="bi-infinity"></i></span>
                  <div class="limit-card-title">No Limit</div>
                  <div class="limit-card-subtitle">Unrestricted usage</div>
                </div>
              </div>
              <div class="col-4">
                <div class="limit-type-card" [class.selected]="selectedLimitType === LimitType.Spend" (click)="selectLimitType(LimitType.Spend)">
                  <span class="limit-card-icon" [class.text-success]="selectedLimitType === LimitType.Spend"><i class="bi-cash-stack"></i></span>
                  <div class="limit-card-title" [class.text-success]="selectedLimitType === LimitType.Spend">Spend Limit</div>
                  <div class="limit-card-subtitle">Restrict by cost</div>
                </div>
              </div>
              <div class="col-4">
                <div class="limit-type-card" [class.selected]="selectedLimitType === LimitType.Data" (click)="selectLimitType(LimitType.Data)">
                  <span class="limit-card-icon" [class.text-success]="selectedLimitType === LimitType.Data"><i class="bi-bar-chart-fill"></i></span>
                  <div class="limit-card-title" [class.text-success]="selectedLimitType === LimitType.Data">Data Usage Limit</div>
                  <div class="limit-card-subtitle">Restrict by volume</div>
                </div>
              </div>
            </div>

            @if (selectedLimitType === LimitType.Spend) {
              <div class="limit-config-area">
                <div class="limit-info-box mb-3">
                  <i class="bi-info-circle-fill me-2"></i>
                  @if (companyUsageLimit) {
                    The <strong class="text-success">Company Level</strong> limit ({{ companyCurrencySymbol }}{{ companyUsageLimit }}) is the default. Toggle below to set a custom limit for the selected SIM(s).
                  } @else {
                    There is no Company Level spend limit set. Toggle below to set a custom limit for the selected SIM(s).
                  }
                </div>

                <div class="limit-config-section mb-3">
                  <div class="d-flex align-items-center mb-2">
                    <span class="fw-semibold">Spend Limit Configuration</span>
                  </div>

                  <div class="limit-config-row">
                    <div>
                      <div class="fw-medium small">Use Custom Limit</div>
                      @if (companyUsageLimit) {
                        <div class="text-muted" style="font-size: 0.78rem;">Override company default ({{ companyCurrencySymbol }}{{ companyUsageLimit }})</div>
                      } @else {
                        <div class="text-muted" style="font-size: 0.78rem;">Set spend limit ({{ companyCurrencySymbol }})</div>
                      }
                    </div>
                    <div class="form-check form-switch mb-0">
                      <input class="form-check-input" type="checkbox" role="switch" style="transform: scale(1.3);"
                             [checked]="useCustomSpendLimit" [disabled]="!companyUsageLimit" (change)="onUseCustomSpendLimitChange($event)">
                    </div>
                  </div>
                  @if (useCustomSpendLimit) {
                    <div class="mt-2 px-1">
                      <div class="input-group">
                        <span class="input-group-text">{{ companyCurrencySymbol }}</span>
                        <input type="number" min="0" class="form-control" placeholder="Enter spend limit"
                               [class.is-invalid]="formSubmitted && !canProceedConfigure()" [(ngModel)]="limitValue">
                      </div>
                      @if (formSubmitted && !canProceedConfigure()) { <div class="invalid-feedback d-block">A value is required</div> }
                    </div>
                  }
                </div>
              </div>
            }

            @if (selectedLimitType === LimitType.Data) {
              <div class="limit-config-area">
                <div class="limit-info-box mb-3">
                  <i class="bi-info-circle-fill me-2"></i>
                  @if (companyDataLimit) {
                    The <strong class="text-success">Company Level</strong> data limit ({{ companyDataLimit }} {{ companyDataWeightCode }}) is the default. Toggle below to set a custom limit for the selected SIM(s).
                  } @else {
                    There is no Company Level data limit set. Toggle below to set a custom limit for the selected SIM(s).
                  }
                </div>

                <div class="limit-config-section mb-3">
                  <div class="d-flex align-items-center mb-2">
                    <span class="fw-semibold">Data Limit Configuration</span>
                  </div>

                  <div class="limit-config-row">
                    <div>
                      <div class="fw-medium small">Use Custom Limit</div>
                      @if (companyDataLimit) {
                        <div class="text-muted" style="font-size: 0.78rem;">Override company default ({{ companyDataLimit }} {{ companyDataWeightCode }})</div>
                      } @else {
                        <div class="text-muted" style="font-size: 0.78rem;">Set data usage limit</div>
                      }
                    </div>
                    <div class="form-check form-switch mb-0">
                      <input class="form-check-input" type="checkbox" role="switch" style="transform: scale(1.3);"
                             [checked]="useCustomDataLimit" [disabled]="!companyDataLimit" (change)="onUseCustomDataLimitChange($event)">
                    </div>
                  </div>
                  @if (useCustomDataLimit) {
                    <div class="mt-2 px-1">
                      <div class="d-flex align-items-stretch gap-2">
                        <input type="number" min="0" class="form-control flex-grow-1" placeholder="Enter data limit"
                               [class.is-invalid]="formSubmitted && !canProceedConfigure()" [(ngModel)]="limitValue">
                        <div class="segmented-toggle" role="group">
                          <input class="segmented-toggle-input" type="radio" id="bulkUnitMbOverride" name="bulkDataUnitOverride" value="MB" [(ngModel)]="dataWeightCode">
                          <label class="segmented-toggle-option" for="bulkUnitMbOverride">MB</label>
                          <input class="segmented-toggle-input" type="radio" id="bulkUnitGbOverride" name="bulkDataUnitOverride" value="GB" [(ngModel)]="dataWeightCode">
                          <label class="segmented-toggle-option" for="bulkUnitGbOverride">GB</label>
                        </div>
                      </div>
                      @if (formSubmitted && !canProceedConfigure()) { <div class="invalid-feedback d-block">A value is required</div> }
                    </div>
                  }
                </div>
              </div>
            }

            @if (selectedLimitType === LimitType.Off) {
              <div class="limit-config-area">
                <div class="limit-info-box mb-0">
                  <i class="bi-info-circle-fill me-2"></i>
                  Usage will be unrestricted for the selected SIM(s).
                </div>
              </div>
            }
          }

          <!-- Change This Month's Limit: one box per limit type actually in use across the selection; each SIM keeps its own type -->
          @if (action === BulkSimAction.ChangeThisMonthLimit) {
            @if (!hasSpendSims && !hasDataSims) {
              <div class="limit-config-area">
                <div class="limit-info-box mb-0">
                  <i class="bi-info-circle-fill me-2"></i>
                  None of the selected SIM(s) have a spend or data limit set, so there is nothing to override this month.
                </div>
              </div>
            } @else {
              <div class="limit-config-area">
                <div class="limit-info-box mb-3">
                  <i class="bi-info-circle-fill me-2"></i>
                  Set a temporary limit for the current month. Each SIM is updated according to its existing limit type.
                  @if (offSimCount > 0) { <span class="text-muted">({{ offSimCount }} selected SIM(s) with no limit set will be skipped.)</span> }
                </div>

                @if (hasSpendSims) {
                  <div class="limit-config-section mb-3">
                    <div class="d-flex align-items-center mb-2">
                      <span class="fw-semibold">This Month's Spend Limit @if (hasDataSims) { <span class="text-muted fw-normal">({{ spendSimCount }} SIM(s))</span> }</span>
                    </div>
                    <div class="limit-config-row">
                      <div>
                        <div class="fw-medium small">Override This Month</div>
                        <div class="text-muted" style="font-size: 0.78rem;">Turn off to clear this month's spend override</div>
                      </div>
                      <div class="form-check form-switch mb-0">
                        <input class="form-check-input" type="checkbox" role="switch" style="transform: scale(1.3);"
                               [checked]="overrideSpendCurrentMonth" (change)="onOverrideSpendCurrentMonthChange($event)">
                      </div>
                    </div>
                    @if (overrideSpendCurrentMonth) {
                      <div class="mt-2 px-1">
                        <div class="input-group">
                          <span class="input-group-text">{{ companyCurrencySymbol }}</span>
                          <input type="number" min="0" class="form-control" placeholder="Enter this month's spend limit"
                                 [class.is-invalid]="formSubmitted && (monthSpendValue === null || monthSpendValue < 0)" [(ngModel)]="monthSpendValue">
                        </div>
                        @if (formSubmitted && (monthSpendValue === null || monthSpendValue < 0)) { <div class="invalid-feedback d-block">A value is required</div> }
                      </div>
                    }
                  </div>
                }

                @if (hasDataSims) {
                  <div class="limit-config-section mb-0">
                    <div class="d-flex align-items-center mb-2">
                      <span class="fw-semibold">This Month's Data Limit @if (hasSpendSims) { <span class="text-muted fw-normal">({{ dataSimCount }} SIM(s))</span> }</span>
                    </div>
                    <div class="limit-config-row">
                      <div>
                        <div class="fw-medium small">Override This Month</div>
                        <div class="text-muted" style="font-size: 0.78rem;">Turn off to clear this month's data override</div>
                      </div>
                      <div class="form-check form-switch mb-0">
                        <input class="form-check-input" type="checkbox" role="switch" style="transform: scale(1.3);"
                               [checked]="overrideDataCurrentMonth" (change)="onOverrideDataCurrentMonthChange($event)">
                      </div>
                    </div>
                    @if (overrideDataCurrentMonth) {
                      <div class="mt-2 px-1">
                        <div class="d-flex align-items-stretch gap-2">
                          <input type="number" min="0" class="form-control flex-grow-1" placeholder="Enter this month's data limit"
                                 [class.is-invalid]="formSubmitted && (monthDataValue === null || monthDataValue < 0)" [(ngModel)]="monthDataValue">
                          <div class="segmented-toggle" role="group">
                            <input class="segmented-toggle-input" type="radio" id="bulkMonthUnitMb" name="bulkMonthDataUnit" value="MB" [(ngModel)]="monthDataWeightCode">
                            <label class="segmented-toggle-option" for="bulkMonthUnitMb">MB</label>
                            <input class="segmented-toggle-input" type="radio" id="bulkMonthUnitGb" name="bulkMonthDataUnit" value="GB" [(ngModel)]="monthDataWeightCode">
                            <label class="segmented-toggle-option" for="bulkMonthUnitGb">GB</label>
                          </div>
                        </div>
                        @if (formSubmitted && (monthDataValue === null || monthDataValue < 0)) { <div class="invalid-feedback d-block">A value is required</div> }
                      </div>
                    }
                  </div>
                }
              </div>
            }
          }

        </div>
      </div>
    }
    <!--#endregion-->

    <!--#region Confirm-->
    @if (step === 'confirm') {
      <div class="alert alert-warning" role="alert">
        <span [innerHtml]="confirmSummary"></span>
      </div>

      <p class="mb-2"><strong>{{ totalCount }}</strong> connection(s) will be affected:</p>

      <div class="table-responsive border rounded" style="max-height: 45vh; overflow-y: auto;">
        <table class="table table-sm table-striped align-middle mb-0">
          <thead class="table-light" style="position: sticky; top: 0;">
            <tr>
              <th>ICCID</th>
              <th>Company</th>
              <th>Assigned User</th>
            </tr>
          </thead>
          <tbody>
            @for (row of runRows; track row.iccid) {
              <tr>
                <td>{{ row.iccid }}</td>
                <td>{{ row.companyName }}</td>
                <td>{{ row.userName }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
    <!--#endregion-->

    <!--#region Progress-->
    @if (step === 'progress') {
      <p class="mb-2">Processing {{ currentIndexDisplay }} of {{ totalCount }}&hellip;</p>

      <div class="progress mb-3" style="height: 22px;">
        <div class="progress-bar progress-bar-striped progress-bar-animated bg-info"
             role="progressbar"
             [style.width.%]="progressPercent"
             [attr.aria-valuenow]="progressPercent"
             aria-valuemin="0"
             aria-valuemax="100">
          {{ progressPercent }}%
        </div>
      </div>

      <div class="d-flex gap-3">
        <span class="text-success"><i class="ri-check-line"></i> Succeeded: {{ successCount }}</span>
        <span class="text-danger"><i class="ri-close-line"></i> Failed: {{ errorCount }}</span>
      </div>
    }
    <!--#endregion-->

    <!--#region Complete-->
    @if (step === 'complete') {
      <div class="text-center mb-4">
        <h4 class="mb-2">Action Complete</h4>
        <p class="text-muted mb-0">
          <span class="text-success">{{ successCount }} succeeded</span>
          @if (errorCount > 0) { <span class="text-danger">, {{ errorCount }} failed</span> }
          of {{ totalCount }}.
        </p>
      </div>

      <div class="table-responsive border rounded" style="max-height: 45vh; overflow-y: auto;">
        <table class="table table-sm table-striped align-middle mb-0">
          <thead class="table-light" style="position: sticky; top: 0;">
            <tr>
              <th>Result</th>
              <th>ICCID</th>
              <th>Company</th>
              <th>Assigned User</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            @for (result of results; track result.iccid) {
              <tr>
                <td>
                  @if (result.ok) {
                    <span class="badge bg-success">Completed</span>
                  } @else {
                    <span class="badge bg-danger">Failed</span>
                  }
                </td>
                <td>{{ result.iccid }}</td>
                <td>{{ result.companyName }}</td>
                <td>{{ result.userName }}</td>
                <td class="text-danger">{{ result.message }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
    <!--#endregion-->

  </div>

  <div class="modal-footer">

    @if (step === 'configure') {
      <button type="button" class="btn btn-light" (click)="cancelForm()">Cancel</button>
      <button type="button" class="btn btn-info add-btn" [disabled]="!canProceedConfigure()" (click)="next()">Next</button>
    }

    @if (step === 'confirm') {
      <button type="button" class="btn btn-light" (click)="cancelForm()">Cancel</button>
      <button type="button" class="btn btn-danger" (click)="confirm()">Confirm</button>
    }

    @if (step === 'progress') {
      <button type="button" class="btn btn-light" disabled>Please wait&hellip;</button>
    }

    @if (step === 'complete') {
      <button type="button" class="btn btn-light" (click)="finishAndClose()">Close</button>
    }

  </div>

</ng-template>
